/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from "react";
import "./SigninScreen.css";
import appIcon from "../../assets/images/sign-in-screen-logo.svg";
import SigninOTPView from "./Views/SigninOTPView";
import ROUTE_PATHS from "../../utils/routePaths";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import useWSMApi from "services/api/useWSMApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import SigninEmailView from "./Views/SigninEmailView";
import SnackbarAlert from "components/elements/SnackbarAlert";
import { USER_TYPE } from "utils/constants";
import { trackLogin } from "utils/analytics";
import { useStorage } from "services/StorageProvider";

const RESEND_DELAY_SECONDS = 120;

const SigninScreen: React.FC = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { refreshUserProfile } = useStorage();
  const unverifiedEmail: string = location.state?.unverified_email;
  const [isOTPSent, setIsOTPSent] = useState<boolean>(false);
  const [otp, setOtp] = useState<string>("");
  const [userEmail, setUserEmail] = useState<string>(unverifiedEmail ?? "");
  const [resendTimer, setResendTimer] = useState<number>(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [externalToken, setExternalToken] = useState<string | null>(null);
  const navigate = useNavigate();
  const { sendLoginOTP, validateLoginOTP, fetchCustomerLead } = useWSMApi();

  const { data: leadData } = useQuery({
    queryKey: ["get-customer-lead", externalToken],
    queryFn: () => fetchCustomerLead(externalToken!),
    enabled: !!externalToken,
  });

  const sendOTPMutation = useMutation({
    mutationFn: () => sendLoginOTP({ email: userEmail, type: USER_TYPE }),
    onSuccess: () => {
      setIsOTPSent(true);
      startResendTimer();
    },
  });

  const validateOTPMutation = useMutation({
    mutationFn: () =>
      validateLoginOTP({
        email: userEmail,
        otp: otp,
        type: USER_TYPE,
      }),
    onSuccess: (data) => {
      localStorage.setItem("apiAccessToken", data?.accessToken);
      refreshUserProfile();
      trackLogin();
      if (unverifiedEmail) {
        navigate(
          ROUTE_PATHS.WSM_CALENDAR_PASS_INTRODUCTION_SCREEN.replace(
            ":stage",
            "1",
          ),
          { replace: true },
        );
      } else {
        navigate(ROUTE_PATHS.WSM_INITIAL_LOAD, { replace: true });
      }
    },
  });

  useEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      setExternalToken(token);
    }
  }, []);

  useEffect(() => {
    if (leadData) {
      setUserEmail(leadData.email);
      sessionStorage.setItem("customerLeadData", JSON.stringify(leadData));
    }
  }, [leadData]);

  useEffect(() => {
    if (unverifiedEmail) {
      setIsOTPSent(true);
      startResendTimer();
    }
  }, [unverifiedEmail]);

  const startResendTimer = () => {
    setResendTimer(RESEND_DELAY_SECONDS);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOTPClick = (): void => {
    sendOTPMutation.mutate();
  };

  const handleResendOTPClick = (): void => {
    if (resendTimer === 0) {
      sendOTPMutation.mutate();
      startResendTimer();
    }
  };

  const handleChangeEmailClick = (): void => {
    setIsOTPSent(false);
    setResendTimer(0);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handleVerifyOTPClick = (): void => {
    validateOTPMutation.mutate();
  };

  const handleOtpChange = (value: string): void => {
    setOtp(value);
  };

  return (
    <div className="wms-mobile-container">
      {sendOTPMutation?.isError ? (
        <SnackbarAlert type={"error"}>
          {sendOTPMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {validateOTPMutation?.isError ? (
        <SnackbarAlert type={"error"}>
          {validateOTPMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      <div className="signin-screen-main-container">
        <img src={appIcon} className="signin-screen-app-logo" alt="logo" />
        <div>
          <div className="signin-screen-text-container">
            <span style={{ color: "#ff6b35" }}>WELCOME TO </span>
            <span>SABEHUB ECOSYSTEM</span>
          </div>
          {isOTPSent ? (
            <div>
              <div className="signin-screen-otp-send-text ">
                OTP sent to your Email ID successfully.
              </div>
              <SigninOTPView
                userEmail={userEmail}
                isAbleToChangeEmail={!unverifiedEmail}
                onClickVerify={handleVerifyOTPClick}
                onChange={handleOtpChange}
                onClickChangeEmail={handleChangeEmailClick}
                isLoading={validateOTPMutation?.isPending}
                resendTimer={resendTimer}
                onClickResendOTP={handleResendOTPClick}
              />
            </div>
          ) : (
            <SigninEmailView
              isLoading={sendOTPMutation?.isPending}
              userEmail={userEmail}
              setUserEmail={setUserEmail}
              onClickSendOTP={handleSendOTPClick}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SigninScreen;
